<template>
    <div class="background-wrapper">

        <v-container max-width="900" class="main-container">
            <!-- ── Skeleton ── -->
            <template v-if="!articlesLoaded">
              <div class="td-skeleton">
                <div class="td-skeleton-hero" />
                <div class="td-skeleton-row" />
                <div class="td-skeleton-row short" />
                <div class="td-skeleton-block" />
              </div>
            </template>
            <div v-else>
                <div v-if="!userStore.isAuthenticated && !teamSearchQuery" class="auth-prompt text-center pa-6">
                    <v-icon size="56" class="mb-3" color="grey">mdi-account-lock-outline</v-icon>
                    <p class="text-body-1 mb-4">{{ t('auth.loginToSeeMyTeams') }}</p>
                    <v-btn color="primary" @click="router.push('/login')">{{ t('auth.loginButton') }}</v-btn>
                </div>
                <TeamItem
                    v-else
                    :articles="nearestArticles" 
                    :publicTeams="publicTeams"
                    :publicTeamsLoading="publicTeamsLoading"
                    :hasMorePublicTeams="hasMorePublicTeams"
                    :searchQuery="teamSearchQuery"
                    @updated="onTeamSaved" 
                    @deleted="onTeamDeleted"
                    @load-more-public="loadMorePublicTeams"
                />
            </div>
        </v-container>

        <!-- Team Form Modal -->
        <TeamFormModal v-model="showTeamFormModal" @saved="onTeamSaved" />

        <!-- Event Form Modal (bottom sheet) -->
        <EventFormModal
          v-model="showAddEventDialog"
          :initialDate="selectedDate"
          @saved="onTeamSaved"
        />

        <!-- Bug Report Dialog -->
        <BugReportDialog v-model:show="showBugReportDialog" />

        <!-- Snackbar -->
        <v-snackbar v-model="showSnackbar" location="top" color="black" timeout="3000">
          <v-icon>mdi-content-copy</v-icon> {{ t('nav.invitationCopied') }}
        </v-snackbar>

        <!-- Filter Sheet -->
        <TeamFilterSheet
          v-model="showFilterSheet"
          :filters="activeFilters"
          :show-auth-sections="userStore.isAuthenticated"
          @apply="onApplyFilters"
        />


    </div>
</template>



<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import axiosInstance from '@/services/axiosInstance';
import { useUserStore } from '@/stores/user/userStore';
import { useMessageStore } from '@/stores/message/messageStore';
import { ITeam } from '@/types';
import TeamItem from '@/components/Articles/TeamItems.vue'
import TeamFormModal from '@/components/Team/TeamFormModal.vue'
import { useQuickActions } from '@/composables/useQuickActions'
import type { QuickActionItem } from '@/stores/quickActions/quickActionsStore'

import { usePermissions } from '@/composables/access.composable'
import { getUserAvatarUrl } from '@/utils/imageUtils'
import { useTheme, useDisplay } from 'vuetify'
import { useNavTransition } from '@/composables/useNavTransition'
import { canAddEvents } from '@/utils/userUtils'
import EventFormModal from '@/components/EventFormModal.vue'
import BugReportDialog from '@/components/BugReportDialog.vue'
import TeamFilterSheet from '@/components/TeamFilterSheet.vue'
import type { TeamFilters } from '@/components/TeamFilterSheet.vue'
const { canCreateContent } = usePermissions()
const { mobile } = useDisplay()
const { activeTab } = useNavTransition()
const { t } = useI18n({ useScope: 'global' })

const router = useRouter();
const route = useRoute();
const isTeamsRoute = computed(() => route.path === '/teams')
const theme = useTheme();
const showTeamFormModal = ref(false);
const showAddEventDialog = ref(false);
const showBugReportDialog = ref(false);
const selectedDate = ref<Date | null>(null);
const canAddEventAllowed = ref(false);
const showSnackbar = ref(false);

// Filter state
const showFilterSheet = ref(false);
const activeFilters = ref<TeamFilters>({
  city: '',
  ageGroup: [],
  visibility: null,
  recruitment: null,
  roles: [],
});

const hasActiveFilters = computed(() => {
  const f = activeFilters.value;
  return !!(f.city || f.ageGroup.length || f.visibility || f.recruitment !== null || f.roles.length);
});

// Inline team search
const teamSearchQuery = ref('');
const userStore = useUserStore();
const messageStore = useMessageStore();

const isDarkMode = computed(() => theme.global.current.value.dark);

onMounted(async () => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});

// Avatar
const userAvatar = computed(() => {
  if (!userStore.user) return null
  return getUserAvatarUrl(userStore.user)
})

const userInitials = computed(() => {
  if (!userStore.user) return ''
  const names = userStore.user.name?.split(' ') || []
  return names.map((n: string) => n[0]).join('').toUpperCase()
})

// Menu items (same as MainMenu drawer)
const quickActionItems = computed<QuickActionItem[]>(() => {
  const items: QuickActionItem[] = []

  if (userStore.isAuthenticated) {
    items.push({ icon: 'mdi-send', label: `${t('nav.messages')}${messageStore.unreadCount > 0 ? ` (${messageStore.unreadCount})` : ''}`, action: 'messages' })
    items.push({ icon: 'mdi-badge-account-outline', label: t('myProfile'), action: 'profile' })
    items.push({ icon: 'mdi-account-group', label: t('nav.myPlayers'), action: 'players' })
    items.push({ icon: 'mdi-account-plus', label: t('nav.createInvitation'), action: 'createInvitation' })
    items.push({ icon: 'mdi-cog-outline', label: t('nav.accountSettings'), action: 'account' })
    items.push({ icon: 'mdi-logout', label: t('nav.logout'), action: 'logout' })
    items.push({ icon: 'mdi-bug', label: t('nav.reportBug'), action: 'bugReport' })
    items.push({ icon: isDarkMode.value ? 'mdi-weather-sunny' : 'mdi-weather-night', label: isDarkMode.value ? t('nav.lightMode') : t('nav.darkMode'), action: 'toggleTheme' })
  }

  return items
})

function onQuickAction(action: string) {
  switch (action) {
    case 'createTeam':
      showTeamFormModal.value = true
      break
    case 'joinTeam':
      // TODO: implement join team flow
      break
    case 'messages':
      router.push('/messages')
      break
    case 'profile':
      openMyProfile()
      break
    case 'players':
      router.push('/players')
      break
    case 'logout':
      localStorage.removeItem('token')
      userStore.clearUser()
      window.location.href = '/login'
      break
    case 'createEvent':
      selectedDate.value = new Date()
      showAddEventDialog.value = true
      break
    case 'createInvitation':
      createInvitation()
      break
    case 'account':
      router.push('/account')
      break
    case 'bugReport':
      showBugReportDialog.value = true
      break
    case 'toggleTheme':
      theme.global.name.value = isDarkMode.value ? 'light' : 'customDarkTheme'
      localStorage.setItem('theme', theme.global.name.value)
      break
  }
}

const qaVisible = computed(() => (!mobile.value || activeTab.value === 1) && isTeamsRoute.value && canCreateContent.value)

const qa = useQuickActions({
    visible: qaVisible.value,
    primaryIcon: 'mdi-plus',
    items: quickActionItems.value,
    avatarSrc: userAvatar.value,
    avatarInitials: userInitials.value,
    showFilter: true,
    filterActive: hasActiveFilters.value,
    showSearch: true,
    searchPlaceholder: t('nav.searchTeam'),
    onPrimary: () => { showTeamFormModal.value = true },
    onAction: onQuickAction,
    onFilter: () => { showFilterSheet.value = true },
    onSearch: onTeamSearchInline,
    onSearchClose: onTeamSearchClose,
})
qa.sync(() => qaVisible.value, 'visible')
qa.sync(() => quickActionItems.value, 'items')
qa.sync(() => hasActiveFilters.value, 'filterActive')
qa.sync(() => userAvatar.value, 'avatarSrc')
qa.sync(() => userInitials.value, 'avatarInitials')
qa.reclaimWhen(() => isTeamsRoute.value && activeTab.value === 1)

async function createInvitation() {
  try {
    const url = `${window.location.origin}/register?referent=${userStore.getUserId}`
    await navigator.clipboard.writeText(url)
    showSnackbar.value = true
  } catch (e) {
    console.error('Nepodařilo se zkopírovat pozvánku:', e)
  }
}

function openMyProfile() {
  if (!userStore.user?._id || !userStore.user?.searchName) return
  const parts = userStore.user.searchName.split(' ')
  if (parts.length < 2) return
  router.push(`/users/${userStore.user._id}-${parts[0]}-${parts[1]}`)
}

const onTeamSaved = async () => {
    // Refresh both team lists after save
    publicTeamsPage.value = 1;
    const [articles, publicResult] = await Promise.all([
        fetchMyTeams(),
        fetchPublicTeams(1),
    ]);
    nearestArticles.value = articles;
    publicTeams.value = publicResult.teams || [];
    hasMorePublicTeams.value = publicResult.hasMore ?? false;
};

const onTeamDeleted = (teamId: string) => {
    // Remove deleted team from list
    nearestArticles.value = nearestArticles.value.filter(t => t._id !== teamId);
};

// --- Inline search handlers ---
function onTeamSearchInline(q: string) {
  teamSearchQuery.value = q;
}

function onTeamSearchClose() {
  teamSearchQuery.value = '';
}

const nearestArticles = ref<ITeam[]>([]);
const articlesLoaded = ref(false);

// Public teams
const publicTeams = ref<ITeam[]>([]);
const publicTeamsPage = ref(1);
const publicTeamsLoading = ref(false);
const hasMorePublicTeams = ref(false);

const path = computed(() => [
    {
        title: t('nav.home'),
        disabled: false,
        href: '/',
    },
    {
        title: t('nav.teams'),
        disabled: true,
        href: '/teams',
    }
])

// Kontrola, zda přihlášený uživatel má roli coach nebo administrator
const canEditContent = computed(() => {
    const userId = userStore.user?._id;
    const roles = userStore.user?.userRoles || [];

    // Uživatel má roli administrátora
    if (roles.includes('administrator')) {
        return true;
    }

    // Uživatel je trenérem u některého z článků
    if (userId) {
        return nearestArticles.value.some(article =>
            Array.isArray(article.coaches) && article.coaches.includes(userId)
        );
    }
    return false;
});




// Funkce pro načtení cl8nku
const fetchMyTeams = async () => {
  // Nepřihlášený uživatel nemá "moje týmy"
  if (!userStore.isAuthenticated) return [];

  try {
    const params: Record<string, any> = {};

    // Search query
    if (teamSearchQuery.value) params.q = teamSearchQuery.value;

    // Apply same filters as public teams
    if (activeFilters.value.city) params.city = activeFilters.value.city;
    if (activeFilters.value.ageGroup.length) params.ageGroup = activeFilters.value.ageGroup.join(',');
    if (activeFilters.value.recruitment !== null) params.recruitment = activeFilters.value.recruitment;
    if (activeFilters.value.roles.length) {
      // "players" in filter means both players + goalkeepers
      const expanded = [...activeFilters.value.roles];
      if (expanded.includes('players') && !expanded.includes('goalkeepers')) {
        expanded.push('goalkeepers');
      }
      params.roles = expanded.join(',');
    }

    const { data } = await axiosInstance.get('/teams/myteams', { params });
    return data.teams || [];
  } catch (error) {
    console.error('Chyba při načítání mých týmů:', error);
    return [];
  }
};

// Načtení veřejných týmů (stránkované)
const fetchPublicTeams = async (page: number = 1) => {
  // Nepřihlášený uživatel bez vyhledávacího dotazu nic nevidí
  if (!userStore.isAuthenticated && !teamSearchQuery.value) {
    return { teams: [], hasMore: false };
  }
  try {
    publicTeamsLoading.value = true;
    const params: Record<string, any> = { page, limit: 10 };
    
    // Search query
    if (teamSearchQuery.value) params.q = teamSearchQuery.value;

    // Apply filters
    if (activeFilters.value.city) params.city = activeFilters.value.city;
    if (activeFilters.value.ageGroup.length) params.ageGroup = activeFilters.value.ageGroup.join(',');
    if (activeFilters.value.visibility) params.visibility = activeFilters.value.visibility;
    if (activeFilters.value.recruitment !== null) params.recruitment = activeFilters.value.recruitment;

    const { data } = await axiosInstance.get('/teams/public', { params });
    return data;
  } catch (error) {
    console.error('Chyba při načítání veřejných týmů:', error);
    return { teams: [], hasMore: false };
  } finally {
    publicTeamsLoading.value = false;
  }
};

const loadMorePublicTeams = async () => {
  if (publicTeamsLoading.value) return;
  publicTeamsPage.value++;
  const result = await fetchPublicTeams(publicTeamsPage.value);
  publicTeams.value = [...publicTeams.value, ...(result.teams || [])];
  hasMorePublicTeams.value = result.hasMore ?? false;
};

async function onApplyFilters(filters: TeamFilters) {
  activeFilters.value = { ...filters };
  publicTeamsPage.value = 1;

  // Role filter only applies to "Moje týmy" — hide public teams when active
  if (filters.roles.length) {
    const myTeams = await fetchMyTeams();
    nearestArticles.value = myTeams;
    publicTeams.value = [];
    hasMorePublicTeams.value = false;
  } else {
    const [myTeams, publicResult] = await Promise.all([
      fetchMyTeams(),
      fetchPublicTeams(1),
    ]);
    nearestArticles.value = myTeams;
    publicTeams.value = publicResult.teams || [];
    hasMorePublicTeams.value = publicResult.hasMore ?? false;
  }
}



// Refetch when search query changes
watch(teamSearchQuery, async () => {
  publicTeamsPage.value = 1;
  const [myTeams, publicResult] = await Promise.all([
    fetchMyTeams(),
    fetchPublicTeams(1),
  ]);
  nearestArticles.value = myTeams;
  publicTeams.value = publicResult.teams || [];
  hasMorePublicTeams.value = publicResult.hasMore ?? false;
});

// Refetch při změně přihlášení (login / logout)
watch(() => userStore.isAuthenticated, async () => {
  publicTeamsPage.value = 1;
  const [myTeams, publicResult] = await Promise.all([
    fetchMyTeams(),
    fetchPublicTeams(1),
  ]);
  nearestArticles.value = myTeams;
  publicTeams.value = publicResult.teams || [];
  hasMorePublicTeams.value = publicResult.hasMore ?? false;

  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});

// Načtení dat při mountování komponenty
onMounted(async () => {
    const [articles, publicResult] = await Promise.all([
        fetchMyTeams(),
        fetchPublicTeams(1),
    ]);
    nearestArticles.value = articles;
    publicTeams.value = publicResult.teams || [];
    hasMorePublicTeams.value = publicResult.hasMore ?? false;
    articlesLoaded.value = true;
});


</script>

<style scoped>
/* ===== BACKGROUND ===== */
.background-wrapper {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
}

.intro {
    font-weight: bold;
}

.auth-prompt {
    margin-top: 32px;
}

.article-content {
    line-height: 1.6;
    font-size: 18px;
}

.edit-btn {
    position: absolute;
    right: 0px;
    top: 0px;
}

.delete-btn {
    position: absolute;
    right: 50px;
    top: 0px;
}

/* ── Skeleton ── */
.td-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 40px;
}
.td-skeleton-hero {
  height: 120px;
  border-radius: 20px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  animation: pulse 1.4s ease-in-out infinite;
}
.td-skeleton-row {
  height: 48px;
  border-radius: 14px;
  background: rgba(var(--v-theme-sheetText), 0.06);
  animation: pulse 1.4s ease-in-out infinite;
  &.short { width: 60%; }
}
.td-skeleton-block {
  height: 200px;
  border-radius: 20px;
  background: rgba(var(--v-theme-sheetText), 0.06);
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
